import React, { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  Box,
  Chip,
  Typography,
  FormControl,
  InputLabel,
  Popover,
  Select,
  MenuItem,
  Tab,
  Tabs,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import TopSection from "../../../../CommonComponents/TopSection";
import ButtonComponent from "../../../../CommonComponents/common/button/Button";
import SectionSpinner from "../../../../CommonComponents/common/Loader/SectionSpinner";
import InviteUserModal from "./bottler admin elm/InviteUserModal";
import { DeleteUserModal } from "./bottler admin elm/DeleteUserModal";
import {
  BottlerElmAccessProvider,
  useBottlerElmAccess,
} from "./BottlerElmAccessContext";
import { bottlerElmAccessStyles } from "./constants/bottlerElmAccessStyles";
import { APPLICATION_TYPE_MAP } from "../../../../constants/userAccess";
import InviteBottlerForm from "./InviteBottlerForm";
import EditBottlerForm from "./EditBottlerForm";
import {
  BOTTLER_ELM_BREADCRUMB,
  BTN_INVITE_USERS,
  APPLICATION_FILTER_LABEL,
  BOTTLER_ONBOARDING_APPLICATIONS,
  PAGE_TITLE,
  PAGE_RIBBON,
  SOP_APPLICATION_NAME,
  TAB_INVITE_USER,
  TAB_INVITED_USERS,
  SELECT_APPLICATION_TITLE,
  SELECT_APPLICATION_MESSAGE,
} from "./constants/bottlerElmAccessText";

const toDisplayList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === null || value === undefined || value === "") return [];
  return [String(value)];
};

const previewChipSx = {
  maxWidth: "100%",
  height: "auto",
  minHeight: 22,
  border: "1px solid #E2E8F0",
  borderRadius: "6px",
  bgcolor: "#F8FAFC",
  color: "#334155",
  fontSize: "0.68rem",
  "& .MuiChip-label": {
    display: "block",
    px: 0.75,
    py: 0.3,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    fontWeight: 600,
  },
};

const ValueSummaryCell = ({ values, title, groupedValues }) => {
  const parsedValues = useMemo(() => toDisplayList(values), [values]);
  // When groupedValues (e.g. { country: [bottlers] }) is provided, the popover lists values by group instead of as a flat list.
  const groupedEntries = useMemo(() => {
    if (!groupedValues || typeof groupedValues !== "object" || Array.isArray(groupedValues)) return [];
    return Object.entries(groupedValues)
      .map(([groupLabel, groupValues]) => [groupLabel, toDisplayList(groupValues)])
      .filter(([, groupValues]) => groupValues.length > 0);
  }, [groupedValues]);
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);
  const previewValues = parsedValues.slice(0, 2);
  const previewText = previewValues.join(", ");

  const handleOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (!parsedValues.length) {
    return <Typography sx={{ color: "#64748B", fontSize: "0.75rem" }}>-</Typography>;
  }

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={handleOpen}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleOpen(event);
          }
        }}
        aria-label={`${parsedValues.length} ${title.toLowerCase()} selected`}
        sx={{
          border: "none",
          p: 0,
          m: 0,
          bgcolor: "transparent",
          width: "100%",
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 0.7,
          minWidth: 0,
        }}
      >
        <Chip
          size="small"
          label={`${parsedValues.length} ${parsedValues.length === 1 ? "item" : "items"}`}
          sx={{
            ...previewChipSx,
            flexShrink: 0,
            "& .MuiChip-label": { ...previewChipSx["& .MuiChip-label"], whiteSpace: "nowrap" },
          }}
        />
        <Typography
          sx={{
            color: "#334155",
            fontSize: "0.74rem",
            fontWeight: 600,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {previewText}{parsedValues.length > 2 ? ", ..." : ""}
        </Typography>
      </Box>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        keepMounted
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableRestoreFocus
        slotProps={{
          paper: {
            onClick: (event) => event.stopPropagation(),
            sx: {
              p: 1,
              width: 360,
              maxWidth: "calc(100vw - 24px)",
              bgcolor: "#FFFFFF",
              border: "1px solid #E2E8F0",
              boxShadow: "0 14px 30px rgba(15, 23, 42, 0.16)",
              borderRadius: "10px",
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.85 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ color: "#334155", fontWeight: 700, fontSize: "0.78rem" }}>{title}</Typography>
            <Chip
              size="small"
              label={`${parsedValues.length} total`}
              sx={{
                ...previewChipSx,
                "& .MuiChip-label": { ...previewChipSx["& .MuiChip-label"], whiteSpace: "nowrap" },
              }}
            />
          </Box>

          <Box
            sx={{
              maxHeight: 260,
              overflowY: "auto",
              overflowX: "hidden",
              display: "flex",
              flexDirection: groupedEntries.length ? "column" : "row",
              flexWrap: groupedEntries.length ? "nowrap" : "wrap",
              gap: groupedEntries.length ? 0.85 : 0.65,
              pr: 0.3,
              scrollbarWidth: "thin",
              scrollbarColor: "#CBD5E1 transparent",
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
              "&::-webkit-scrollbar-thumb": { bgcolor: "#CBD5E1", borderRadius: 999 },
              "&::-webkit-scrollbar-thumb:hover": { bgcolor: "#94A3B8" },
            }}
          >
            {groupedEntries.length ? groupedEntries.map(([groupLabel, groupValues]) => (
              <Box key={groupLabel} sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                <Typography sx={{ color: "#334155", fontWeight: 700, fontSize: "0.68rem" }}>{groupLabel}</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.65 }}>
                  {groupValues.map((item) => (
                    <Chip key={`${groupLabel}-${item}`} size="small" label={item} sx={previewChipSx} />
                  ))}
                </Box>
              </Box>
            )) : parsedValues.map((item) => (
              <Chip key={item} size="small" label={item} sx={previewChipSx} />
            ))}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const BottlerElmAccessInner = () => {
  const { rowData, tableLoading, domainList, getElmUserList, inviteUsers, deleteUser, editUser, getDomainList } =
    useBottlerElmAccess();

  const gridRef = useRef(null);
  const [openInviteUserModal, setOpenInviteUserModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedDeleteRow, setSelectedDeleteRow] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editModeData, setEditModeData] = useState(null);
  const [editingBottler, setEditingBottler] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  const selectedApplicationType =
    APPLICATION_TYPE_MAP[selectedApplication] || "";
  const selectedApplicationTypeRef = useRef(selectedApplicationType);
  selectedApplicationTypeRef.current = selectedApplicationType;
  const isSopApplication = selectedApplication === SOP_APPLICATION_NAME;
  const showOnboardingTabs = !selectedApplicationType || isSopApplication;

  const loggedInUserEmail = localStorage.getItem("userEmail") || "";

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [10, 25, 50, 100];

  useEffect(() => {
    setOpenInviteUserModal(false);
    setOpenDeleteModal(false);
    setSelectedDeleteRow(null);
    setIsEditMode(false);
    setEditModeData(null);
    setEditingBottler(null);
    setActiveTab(0);
    getElmUserList(selectedApplicationType);
  }, [getElmUserList, selectedApplicationType]);

  const onGridReady = (params) => {
    gridRef.current = params.api;
  };

  const defaultColDef = useMemo(
    () => ({ sortable: true, filter: "agMultiColumnFilter", resizable: true }),
    []
  );

  const onClickDeleteRow = (rowParams) => {
    setOpenDeleteModal(true);
    setSelectedDeleteRow(rowParams);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setSelectedDeleteRow(null);
  };

  const handleDeleteConfirmation = async () => {
    if (!selectedApplicationType) return;

    setOpenDeleteModal(false);
     const payload = {
      requestedUserEmail: selectedDeleteRow?.data?.userEmail.trim(),
      requestedForUserName: selectedDeleteRow?.data?.requestedForUserName,
      region: selectedDeleteRow?.data?.region,
      countryList: selectedDeleteRow?.data?.countryList,
      bottlerGroups: selectedDeleteRow?.data?.bottlerGroups,
      accessLevel: selectedDeleteRow?.data?.role,
      applicationType: selectedApplicationType,
      // requestedBy: loggedInUserEmail,
    };
    await deleteUser([payload], selectedApplicationType);
    setSelectedDeleteRow(null);
  };

  const onClickEdit = async (rowParams) => {
    if (!selectedApplicationType) return;

    if (isSopApplication) {
      setEditingBottler(rowParams.data);
      return;
    }

    setIsEditMode(true);
    await getDomainList(selectedApplicationType);
    setOpenInviteUserModal(true);
    setEditModeData(rowParams.data);
  };

  // Same edit entry point as the grid's edit icon, triggered from the Invite tab's
  // duplicate-invite warning instead of a grid row.
  const handleEditExistingBottler = (existingRow) => {
    if (!selectedApplicationType || !isSopApplication) return;
    setActiveTab(1);
    setEditingBottler(existingRow);
  };

  const handleCloseInviteUserModal = () => {
    setOpenInviteUserModal(false);
    setIsEditMode(false);
    setEditModeData(null);
  };

  const onClickInviteUsers = async () => {
    if (!selectedApplicationType) return;

    await getDomainList(selectedApplicationType);
    setOpenInviteUserModal(true);
  };

  const handleSubmitInviteUserModal = async (rows) => {
    if (!selectedApplicationType) return;

    const inviteList = rows.map(row => ({
      requestedUserEmail: row.email.trim(),
      region: row.region,
      bottlerGroups: row.bottlerGroups,
      accessLevel: row.accessLevel,
      applicationType: selectedApplicationType,
      // requestedBy: loggedInUserEmail,
    }));
    handleCloseInviteUserModal();
    await inviteUsers(inviteList, selectedApplicationType);
  };

  const handleUpdateEditDetailsModal = async (row) => {
    if (!selectedApplicationType) return;

    const payload = {
      requestedUserEmail: row.email.trim(),
      region: row.region,
      bottlerGroups: row.bottlerGroups,
      accessLevel: row.accessLevel,
      applicationType: selectedApplicationType,
      // requestedBy: loggedInUserEmail,
    };
    handleCloseInviteUserModal();
    await editUser([payload], selectedApplicationType);
  };

  const handleEditUpdated = async () => {
    const applicationType = selectedApplicationType;
    if (!applicationType || selectedApplicationTypeRef.current !== applicationType) return;

    const refreshedRows = await getElmUserList(applicationType, {
      preserveExisting: true,
    });

    if (
      Array.isArray(refreshedRows) &&
      selectedApplicationTypeRef.current === applicationType
    ) {
      setEditingBottler(null);
    }
  };

 const wrapColumnProps = {
  wrapText: true,
  autoHeight: true,
  cellStyle: { whiteSpace: "normal", wordBreak: "break-word", lineHeight: "1.4", padding: "8px" },
 };

 const columns = [
  {
    field: "userEmail",
    headerName: "Email",
    flex: 1.5,
    minWidth: 160,
    ...wrapColumnProps,
  },
  {
    field: "region",
    headerName: "Region",
    flex: 1.5,
    minWidth: 160,
    ...wrapColumnProps,
  },
  {
    field: "countryList",
    headerName: "Country",
    flex: 1.5,
    minWidth: 160,
    cellStyle: { display: "flex", alignItems: "center" },
    valueGetter: (params) => toDisplayList(params?.data?.countryList).join(", "),
    cellRenderer: (params) => <ValueSummaryCell values={params?.data?.countryList} title="Countries" />,
  },
  {
    field: "bottlerGroups",
    headerName: "Bottler List",
    flex: 1.5,
    minWidth: 160,
    cellStyle: { display: "flex", alignItems: "center" },
    valueGetter: (params) => toDisplayList(params?.data?.bottlerGroups).join(", "),
    cellRenderer: (params) => (
      <ValueSummaryCell
        values={params?.data?.bottlerGroups}
        groupedValues={params?.data?.bottlers}
        title="Bottlers"
      />
    ),
  },
  {
    field: "ticketId",
    headerName: "Ticket ID",
    flex: 1.5,
    minWidth: 160,
    ...wrapColumnProps,
    cellRenderer: (params) => {
      const { ticketId, ticketUrl } = params?.data || {};
      if (!ticketId) return "-";
      const idText = String(ticketId);
      if (typeof ticketUrl !== "string" || !/^https?:\/\//i.test(ticketUrl)) return idText;
      return (
        <a href={ticketUrl} target="_blank" rel="noopener noreferrer">
          {idText}
        </a>
      );
    },
  },
  {
    field: "ticketStatus",
    headerName: "Ticket Status",
    flex: 1.5,
    minWidth: 160,
    ...wrapColumnProps,
    valueFormatter: (params) => (params.value ? String(params.value) : "-"),
  },
  {
    field: "createdOn",
    headerName: "Invited On",
    flex: 1.5,
    minWidth: 160,
    ...wrapColumnProps,
    valueFormatter: (params) =>
      params.value
        ? new Date(params.value).toLocaleDateString()
        : "",
  },
  {
    field: "actions",
    headerName: "Actions",
    cellRenderer: (response) => (
      <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
        <EditIcon
          onClick={() =>
            response?.data?.userEmail !== loggedInUserEmail &&
            onClickEdit(response)
          }
          style={{
            cursor:
              response?.data?.userEmail !== loggedInUserEmail
                ? "pointer"
                : "default",
            color:
              response?.data?.userEmail !== loggedInUserEmail
                ? "#000"
                : "#bcbcbc",
          }}
        />

        <DeleteIcon
          onClick={() =>
            response?.data?.userEmail !== loggedInUserEmail &&
            onClickDeleteRow(response)
          }
          style={{
            cursor:
              response?.data?.userEmail !== loggedInUserEmail
                ? "pointer"
                : "default",
            color:
              response?.data?.userEmail !== loggedInUserEmail
                ? "#000"
                : "#bcbcbc",
          }}
        />
      </div>
    ),
    pinned: "right",
    lockPinned: true,
    lockPosition: true,
    sortable: false,
    filter: false,
    width: 120,
  },
];

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <TopSection
        breadcrumbArray={BOTTLER_ELM_BREADCRUMB}
        hideHeader={true}
        headerName=""
        hideTitle={true}
      />

      <Box sx={bottlerElmAccessStyles.applicationFilter}>
        <FormControl size="small" fullWidth>
          <InputLabel id="bottler-onboarding-application-label">
            {APPLICATION_FILTER_LABEL}
          </InputLabel>
          <Select
            labelId="bottler-onboarding-application-label"
            id="bottler-onboarding-application"
            value={selectedApplication}
            label={APPLICATION_FILTER_LABEL}
            onChange={(event) => setSelectedApplication(event.target.value)}
            sx={{ color: "#000" }}
          >
            {BOTTLER_ONBOARDING_APPLICATIONS.map((application) => (
              <MenuItem key={application} value={application}>
                {application}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {showOnboardingTabs && (
        <>
          <Box sx={bottlerElmAccessStyles.tabNavigation}>
            <Tabs
              value={activeTab}
              onChange={(_, nextTab) => {
                setActiveTab(nextTab);
                if (nextTab !== 1) setEditingBottler(null);
              }}
              aria-label="Bottler onboarding sections"
              sx={{
                ...bottlerElmAccessStyles.tabs,
                ...(!selectedApplicationType && {
                  '& .MuiTabs-indicator': { backgroundColor: '#64748B' },
                }),
              }}
            >
              <Tab
                id="bottler-onboarding-tab-0"
                aria-controls="bottler-onboarding-panel-0"
                label={TAB_INVITE_USER}
                disabled={!selectedApplicationType}
                sx={bottlerElmAccessStyles.tab}
              />
              <Tab
                id="bottler-onboarding-tab-1"
                aria-controls="bottler-onboarding-panel-1"
                label={TAB_INVITED_USERS}
                disabled={!selectedApplicationType}
                sx={bottlerElmAccessStyles.tab}
              />
            </Tabs>
          </Box>

          <Box
            component="section"
            role="tabpanel"
            id="bottler-onboarding-panel-0"
            aria-labelledby="bottler-onboarding-tab-0"
            hidden={activeTab !== 0}
            sx={bottlerElmAccessStyles.tabPanel}
          >
            {isSopApplication ? (
              <InviteBottlerForm
                requestedByEmail={loggedInUserEmail}
                applicationType={selectedApplication}
                existingBottlerInvites={rowData}
                onEditExisting={handleEditExistingBottler}
                onViewRequestStatus={() => {
                  setActiveTab(1);
                  if (selectedApplicationType) getElmUserList(selectedApplicationType);
                }}
              />
            ) : (
              <Box role="status" sx={bottlerElmAccessStyles.emptyState}>
                <InfoIcon sx={bottlerElmAccessStyles.emptyStateIcon} />
                <Typography sx={bottlerElmAccessStyles.emptyStateTitle}>
                  {SELECT_APPLICATION_TITLE}
                </Typography>
                <Typography sx={bottlerElmAccessStyles.emptyStateMessage}>
                  {SELECT_APPLICATION_MESSAGE}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      {selectedApplicationType && (
        <Box
          component={isSopApplication ? "section" : "div"}
          role={isSopApplication ? "tabpanel" : undefined}
          id={isSopApplication ? "bottler-onboarding-panel-1" : undefined}
          aria-labelledby={isSopApplication ? "bottler-onboarding-tab-1" : undefined}
          hidden={isSopApplication && activeTab !== 1}
          sx={bottlerElmAccessStyles.tabPanel}
        >
          {(!isSopApplication || activeTab === 1) && (
            isSopApplication && editingBottler ? (
              <EditBottlerForm
                requestedByEmail={loggedInUserEmail}
                applicationType={selectedApplication}
                editData={editingBottler}
                onCancel={() => setEditingBottler(null)}
                onUpdated={handleEditUpdated}
              />
            ) : <>
            {!isSopApplication && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="h6" fontWeight={700} color="#000">
                  {PAGE_TITLE}
                </Typography>
                <ButtonComponent
                  type="secondary"
                  maxWidth="150px"
                  label={BTN_INVITE_USERS}
                  height="40px"
                  onClick={onClickInviteUsers}
                  disabled={!selectedApplicationType}
                />
              </Box>
            )}
            <Box sx={bottlerElmAccessStyles.ribbon}>
              <InfoIcon sx={bottlerElmAccessStyles.ribbonIcon} />
              <Typography sx={bottlerElmAccessStyles.ribbonText}>
                {PAGE_RIBBON}
              </Typography>
            </Box>

            <Box sx={bottlerElmAccessStyles.gridWrapper}>
              <div className="ag-theme-quartz" style={{ height: 450, width: "100%" }}>
                <AgGridReact
                  ref={gridRef}
                  rowData={rowData}
                  columnDefs={columns}
                  defaultColDef={defaultColDef}
                  onGridReady={onGridReady}
                  pagination={pagination}
                  paginationPageSize={paginationPageSize}
                  paginationPageSizeSelector={paginationPageSizeSelector}
                />
              </div>
              {tableLoading && <SectionSpinner />}
            </Box>
            </>
          )}
        </Box>
      )}

      <InviteUserModal
        openInviteUserModal={openInviteUserModal}
        handleCloseInviteUserModal={handleCloseInviteUserModal}
        handleSubmitInviteUserModal={handleSubmitInviteUserModal}
        isEditMode={isEditMode}
        editModeData={editModeData}
        handleUpdateEditDetailsModal={handleUpdateEditDetailsModal}
        submitLoading={tableLoading}
        domainList={domainList}
      />

      <DeleteUserModal
        openDeleteConfirmationModalData={openDeleteModal}
        handleCloseDeleteModal={handleCloseDeleteModal}
        handleDeleteConfirmation={handleDeleteConfirmation}
      />

    </Box>
  );
};

const BottlerElmAccess = () => (
  <BottlerElmAccessProvider>
    <BottlerElmAccessInner />
  </BottlerElmAccessProvider>
);

export default BottlerElmAccess;
